import {QueryClient, useQueryClient} from '@tanstack/react-query';
import {useEffect, useRef, useState} from 'react';
import {OFF_PLATFORM_INVALID_TOKEN} from '@/utils/offPlatformShareToken';

function isInvalidShareTokenError(error: unknown): boolean {
  return (error as Error | null)?.message === OFF_PLATFORM_INVALID_TOKEN;
}

/**
 * True when any cached query or mutation is failing with the off-platform
 * share-token sentinel (a 401 -> OFF_PLATFORM_INVALID_TOKEN thrown by
 * jsonapiClient).
 *
 * For queries we check `fetchFailureReason` as well as the terminal `error`:
 * react-query only moves a query into the `error` state after its retries are
 * exhausted, and the off-platform campaign query retries indefinitely — so the
 * sentinel shows up on `fetchFailureReason` (the latest failure) on the very
 * first 401, long before (or instead of) a terminal error.
 *
 * We also scan the mutation cache: when the token expires while the page stays
 * open, the next guarded request can be a mutation (a comment or approval
 * submitted from OffPlatformReviewFooter) rather than a query, and its sentinel
 * error lands only in the mutation cache.
 */
function hasInvalidShareTokenError(queryClient: QueryClient): boolean {
  const queryHit = queryClient
    .getQueryCache()
    .getAll()
    .some((query) => {
      const {error, fetchFailureReason} = query.state;
      return (
        isInvalidShareTokenError(error) ||
        isInvalidShareTokenError(fetchFailureReason)
      );
    });
  if (queryHit) {
    return true;
  }

  return queryClient
    .getMutationCache()
    .getAll()
    .some((mutation) => isInvalidShareTokenError(mutation.state.error));
}

/**
 * Detects an expired/invalid off-platform share link by watching the query
 * cache for the OFF_PLATFORM_INVALID_TOKEN sentinel. On the first sighting it
 * latches `isExpired` and cancels all in-flight queries, which also aborts
 * their pending retries — so a bad token resolves to a single empty state
 * instead of a retry storm (and the indefinitely-retrying campaign query stops
 * hammering the API in the background).
 *
 * Mounted only by OffPlatformLayout, so the cache subscription exists only on
 * off-platform routes — it adds no cost to the rest of the app.
 */
function useOffPlatformShareTokenStatus() {
  const queryClient = useQueryClient();
  const [isExpired, setIsExpired] = useState(() =>
    hasInvalidShareTokenError(queryClient)
  );
  const handledRef = useRef(false);

  useEffect(() => {
    const handleInvalidToken = () => {
      if (handledRef.current) {
        return;
      }
      handledRef.current = true;
      setIsExpired(true);
      // Stop the in-flight requests and their pending retries. revert:false
      // keeps the sentinel error in the cache (the v4 default would roll it
      // back), so detection stays consistent. Fire-and-forget: we don't await
      // cancellation, and a rejection here shouldn't surface as unhandled.
      queryClient.cancelQueries(undefined, {revert: false}).catch(() => {});
    };

    if (hasInvalidShareTokenError(queryClient)) {
      handleInvalidToken();
      return undefined;
    }

    // Watch both caches: query failures (refetches in the background) and
    // mutation failures (a comment/approval submitted after the token expires)
    // can each be the first sighting of the sentinel.
    const check = () => {
      if (hasInvalidShareTokenError(queryClient)) {
        handleInvalidToken();
        unsubscribeAll();
      }
    };
    const unsubscribeQuery = queryClient.getQueryCache().subscribe(check);
    const unsubscribeMutation = queryClient.getMutationCache().subscribe(check);
    function unsubscribeAll() {
      unsubscribeQuery();
      unsubscribeMutation();
    }

    return unsubscribeAll;
  }, [queryClient]);

  return {isExpired};
}

export default useOffPlatformShareTokenStatus;
